import { LoadingIndicator } from "@/app/(shop)/services/[slug]/components/loadingIndicator";
import { Merchant } from "@/dto/merchant";
import { isEmail, required } from "@/utils/inputValidators";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { inputFieldTheme } from "@/utils/themes";
import useMediaQuery from "@/utils/useMediaQuery";
import { useTranslations } from "next-intl";
import { UseFormReturn } from "react-hook-form";
import { ProfileIcon } from "../_icons/profileIcon";
import LocalizedPhoneInput from "../_localized/localizedPhoneInput";
import { ConfirmButton } from "../button/confirmButton";
import { TextButton } from "../button/textButton";
import { StyledInputField } from "../styledInputField/styledInputField";
import { LoginFormFields } from "@/types";
import { useEffect, useRef } from "react";

const perksEnabledLocales = ["en-US"];

export const renderLoginModalSwitchInput = (
  chunks: React.ReactNode,
  setErrorMessage: (message: string | null) => void,
  showPhoneNumberInput: boolean,
  setShowPhoneNumberInput: (show: boolean) => void,
  merchantColor: string
) => (
  <button
    className="font-semibold cursor-pointer"
    style={{ color: merchantColor }}
    data-testid={applyDataTestId(pom.modals.loginModalSwitchInput)}
    onClick={() => {
      setErrorMessage(null);
      setShowPhoneNumberInput(!showPhoneNumberInput);
    }}
  >
    {chunks}
  </button>
);

export const LoginModalUsernameInput = ({
  merchant,
  form,
  setErrorMessage,
  merchantColor,
  showPhoneNumberInput,
  showEmailSwitching,
  setShowPhoneNumberInput,
  errorMessage,
  isLoading,
  isValid,
  // eslint-disable-next-line no-unused-vars
  captchaRef,
  showGuestCheckout,
  handleGuestCheckout,
  onLogin,
  recaptchaEnabled,
  titleId,
  subtitleId,
}: {
  merchant: Merchant;
  form: UseFormReturn<LoginFormFields>;
  // eslint-disable-next-line no-unused-vars
  setErrorMessage: (message: string | null) => void;
  merchantColor: string;
  showPhoneNumberInput: boolean;
  showEmailSwitching: boolean;
  // eslint-disable-next-line no-unused-vars
  setShowPhoneNumberInput: (show: boolean) => void;
  errorMessage: string | null;
  isLoading: boolean;
  isValid: boolean;
  // eslint-disable-next-line no-unused-vars
  captchaRef: any;
  showGuestCheckout: boolean;
  handleGuestCheckout: () => void;
  // eslint-disable-next-line no-unused-vars
  onLogin: (token: string) => void;
  recaptchaEnabled: boolean;
  titleId: string;
  subtitleId: string;
}) => {
  const t = useTranslations("LoginModalUsernameInput");
  const localizedEmailRequiredMessage = t("emailErrorRequirement");

  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const perksEnabled = perksEnabledLocales.includes(merchant?.locale);
  const prevShowPhoneMode = useRef(showPhoneNumberInput);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prevShowPhoneMode.current === showPhoneNumberInput) {
      return;
    }

    prevShowPhoneMode.current = showPhoneNumberInput;

    form.clearErrors();
    const targetField = showPhoneNumberInput ? "phone" : "email";

    form.resetField(targetField);

    const timer = setTimeout(() => {
      const inputElement = inputContainerRef.current?.querySelector("input");
      if (inputElement) {
        inputElement.focus();
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [showPhoneNumberInput, form]);

  return (
    <div
      data-testid={applyDataTestId(pom.loginInfoModal.phoneInput)}
      className="px-4 pb-4 flex flex-col justify-center items-center max-h-[calc(100vh-6rem)]"
    >
      <span className="shrink-0">
        <ProfileIcon brandingColor={merchantColor} />
      </span>
      <h2
        id={titleId}
        tabIndex={-1}
        aria-describedby={subtitleId}
        className="mt-4 text-3xl font-medium text-center text-neutral-700 shrink"
      >
        {t("title")}
      </h2>
      <p
        id={subtitleId}
        className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 sm:w-[460px] shrink"
      >
        {perksEnabled ? t("perksMessage") : t("noPerksMessage")}
      </p>
      <div
        ref={inputContainerRef}
        className="my-5 w-full sm:w-1/2 flex flex-col items-center shrink"
      >
        {/* TODO: ADD NEGATIVE FLOWS TO THESE INPUTS */}
        {showPhoneNumberInput && (
          <LocalizedPhoneInput
            name="phone"
            form={form}
            locale={merchant.locale}
            merchantColor={merchantColor}
            srLabel={t("enterPhoneNumberLabel")}
          />
        )}
        {!showPhoneNumberInput && (
          <StyledInputField
            label={t("email")}
            name="email"
            form={form}
            validator={{
              isEmail: isEmail(localizedEmailRequiredMessage),
              required: required(t("email")),
            }}
            theme={inputFieldTheme}
            srLabel={t("enterEmailLabel")}
          />
        )}
      </div>
      {showEmailSwitching && (
        <div>
          <p className="font-normal text-[15px] text-center">
            {t.rich(
              showPhoneNumberInput
                ? "signUpOrLoginWithYourEmail"
                : "signUpOrLoginWithYourPhone",
              {
                action: (chunks) =>
                  renderLoginModalSwitchInput(
                    chunks,
                    setErrorMessage,
                    showPhoneNumberInput,
                    setShowPhoneNumberInput,
                    merchantColor
                  ),
              }
            )}
          </p>
        </div>
      )}
      {errorMessage && (
        <div className="text-red-600 text-sm mt-2 shrink">{errorMessage}</div>
      )}
      {isLoading ? (
        <div className="fixed bottom-0 sm:static">
          <LoadingIndicator />
        </div>
      ) : (
        <ConfirmButton
          disabled={!isValid}
          style={{
            backgroundColor: !isValid ? "#C7C7C7" : merchantColor,
          }}
          className="rounded-none fixed sm:static bottom-0 w-full px-16 py-5 sm:mt-11 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] sm:rounded-lg sm:w-[250px] max-md:px-5 sm:max-md:mt-10 shrink"
          onClick={() => {
            if (errorMessage) {
              setErrorMessage(null);
            }
            if (recaptchaEnabled) {
              captchaRef.current?.reset();
              captchaRef.current?.execute();
            } else {
              onLogin("mock-token");
            }
          }}
        >
          <p>{t("continue")}</p>
        </ConfirmButton>
      )}
      {showGuestCheckout && (
        <TextButton
          className={`fixed sm:static bottom-16 border-t border-[#e5e5e5] sm:border-none sm:mt-8 sm:mb-6 text-xl font-medium leading-7 text-center max-md:mt-10 ${
            isMobileScreen && "w-full h-16 rounded-none"
          }`}
          onClick={handleGuestCheckout}
          style={{ color: merchantColor }}
          data-testid={applyDataTestId(
            pom.modals.loginModalGuestCheckoutButton
          )}
        >
          {t("guestCheckout")}
        </TextButton>
      )}
    </div>
  );
};
